import { client } from './api';
import type {
  DashboardTab,
  DashboardTabWidgetItem,
  ReportDefinitionPayload,
  ReportListItem,
  ReportWidget,
  ReportWidgetPayload,
  ReportingManifest,
} from '../types/reporting';
import { SharedCancellation } from '../utils/sharedCancellation';

export const getReportingManifest = async (): Promise<ReportingManifest> => {
  const response = await client.request({
    method: 'GET',
    url: '/reporting/manifest',
    throwOnError: true,
  });
  return response.data as ReportingManifest;
};

export interface ExtractResult {
  buffer: ArrayBuffer;
  etag: string;
  builtAt: string | null;
}

// ---------------------------------------------------------------------------
// Scope contract (SCALING-DESIGN §8, decision D1)
// ---------------------------------------------------------------------------
// `GET /reporting/extracts/{key}` takes a DISCRIMINATED scope:
//
//   scope=full     — the whole dataset. No predicate params allowed. This is
//                    also the server default when `scope` is omitted, so a full
//                    request sends NO scope params at all and the URL stays
//                    byte-identical to the pre-scope client.
//   scope=primary  — one conjunctive predicate on one axis, used ONLY as a
//                    partition-pruning hint: `axis`, `kind`, `lower`/`upper`
//                    and their inclusivity flags.
//
// This is NOT a security boundary. The browser authored the WHERE and executes
// it locally; row scope is enforced server-side by agency isolation PLUS the
// clinic row filter (a slug granted at a clinic serves only that clinic's rows;
// granted agency-wide it serves all of them), and column scope by
// served_columns. The server therefore validates SHAPE only and FAILS
// OPEN — an unknown axis or an unparseable bound means "no pruning", never a
// 4xx. Fail-open is the required safety direction: over-serving partitions is
// harmless (DuckDB re-filters semantically), under-serving silently drops rows.
//
// `kind` carries the full D1 vocabulary so the wire type documents the contract,
// but the client only ever CONSTRUCTS 'range': an `eq` date filter is encoded as
// a degenerate inclusive range (lower === upper), which prunes identically under
// the server's min/max overlap test and keeps one encoding to reason about.
export type ScopePredicateKind = 'range' | 'equality' | 'is_null';

export interface PrimaryScope {
  scope: 'primary';
  axis: string;
  kind: ScopePredicateKind;
  // ISO `YYYY-MM-DD` bounds. Either may be absent (a half-open predicate such as
  // `>= 2026-01-01`); at least one is always present, or the caller should have
  // used FULL_SCOPE instead.
  lower?: string;
  upper?: string;
  lowerInclusive?: boolean;
  upperInclusive?: boolean;
}

export type ExtractScope = { scope: 'full' } | PrimaryScope;

export const FULL_SCOPE: ExtractScope = { scope: 'full' };

export interface ExtractPopulation {
  includeTestRows: boolean;
  includeInactiveEpisodes: boolean;
  includeImportedPatients?: boolean;
  // Client-side only: the extract holds every claim and the report query drops
  // the invoice ones. It is not sent to the server or part of the cache identity.
  excludeInvoiceClaims?: boolean;
}

export const DEFAULT_EXTRACT_POPULATION: ExtractPopulation = {
  includeTestRows: false,
  includeInactiveEpisodes: false,
  includeImportedPatients: false,
};

export const populationFingerprint = (
  population: ExtractPopulation = DEFAULT_EXTRACT_POPULATION
): string =>
  `test=${population.includeTestRows ? '1' : '0'}|inactive=${population.includeInactiveEpisodes ? '1' : '0'}|imp=${population.includeImportedPatients ? '1' : '0'}`;

// Canonical, stable serialization of a scope — the REPRESENTATION IDENTITY that
// every per-dataset key in this module (HTTP cache, plain in-flight dedup, force
// coalescing, force generation) is partitioned by, and that duckdbEngine reuses
// for its view registration.
//
// Without it, two scopes of one dataset collide: their cached bytes overwrite
// each other, two in-flight fetches coalesce into one, and one scope's Parquet
// answers the other's query. That is the client-side half of the D3 ETag fix —
// the server can identify the exact scoped representation all it likes, but a
// browser that keys by dataset alone will still hand back the wrong bytes.
//
// Fields are emitted in a fixed order with explicit defaults (never `undefined`
// holes), so two structurally equal scopes always fingerprint equal regardless
// of object key order or optional-field presence.
export const scopeFingerprint = (scope: ExtractScope = FULL_SCOPE): string => {
  if (scope.scope === 'full') return 'full';
  return [
    'primary',
    scope.axis,
    scope.kind,
    scope.lower ?? '',
    scope.upper ?? '',
    scope.lowerInclusive === false ? 'lx' : 'li',
    scope.upperInclusive === false ? 'ux' : 'ui',
  ].join('|');
};

// Per-(dataset, scope, population) key for every map below. NUL can't occur in
// a dataset key or fingerprint field, so the join is unambiguous.
export type ExtractPipeline = 'patient_roster' | 'pcr';

const requestKey = (
  datasetKey: string,
  scope: ExtractScope,
  population: ExtractPopulation,
  pipeline?: ExtractPipeline
): string =>
  `${datasetKey}\u0000${scopeFingerprint(scope)}\u0000${populationFingerprint(population)}${pipeline ? `\u0000${pipeline}` : ''}`;

// A FULL request emits no scope params, so the URL is unchanged from the
// pre-scope client — important while the server-side D1 handler rolls out.
const extractUrl = (
  datasetKey: string,
  scope: ExtractScope,
  opts?: {
    refresh?: boolean;
    since?: string;
    population?: ExtractPopulation;
    pipeline?: ExtractPipeline;
  }
): string => {
  const params = new URLSearchParams();
  if (opts?.refresh) params.set('refresh', '1');
  if (opts?.since) params.set('since', opts.since);
  if (opts?.pipeline) params.set('pipeline', opts.pipeline);
  if (scope.scope === 'primary') {
    params.set('scope', 'primary');
    params.set('axis', scope.axis);
    params.set('kind', scope.kind);
    if (scope.lower !== undefined) {
      params.set('lower', scope.lower);
      params.set('lower_inclusive', String(scope.lowerInclusive !== false));
    }
    if (scope.upper !== undefined) {
      params.set('upper', scope.upper);
      params.set('upper_inclusive', String(scope.upperInclusive !== false));
    }
  }
  const population = opts?.population ?? DEFAULT_EXTRACT_POPULATION;
  // False is the server default, so legacy/default requests keep their existing
  // URLs. True opt-ins are explicit and travel on primary and related requests.
  if (population.includeTestRows) params.set('include_test_rows', 'true');
  if (population.includeInactiveEpisodes) {
    params.set('include_inactive_episodes', 'true');
  }
  const query = params.toString();
  return `/reporting/extracts/${datasetKey}${query ? `?${query}` : ''}`;
};

// Poll cadence for the async-build `202 building` contract (D-202 §6). The
// server's `Retry-After` header is the authoritative FLOOR; the client grows its
// own backoff on top of it, capped, so a slow build never hammers the endpoint.
const POLL_BASE_MS = 2000; // fallback when a 202 carries no/invalid Retry-After
const POLL_MAX_MS = 10000; // ceiling for the client backoff between polls
// After this the loop keeps polling at max cadence but flags `slow` so the UI
// copy can read "taking longer than usual…". It NEVER fails the poll — there is
// no hard client timeout; a terminally-failed build surfaces via 503, not a
// timeout (D-202 §6/§8).
const SOFT_SLOW_MS = 180000;

// Progress surfaced on each 202 poll. The report runner supplies this callback
// only for an initial primary-extract build; refreshes and loaded-result
// revalidation keep their existing loading surfaces.
export interface BuildingProgress {
  elapsedMs: number;
  slow: boolean;
}
type BuildingCallback = (progress: BuildingProgress) => void;

export interface FetchExtractOptions {
  force?: boolean;
  onBuilding?: BuildingCallback;
  // Omitted = FULL_SCOPE (the whole dataset), matching the server default.
  scope?: ExtractScope;
  // Omitted = both population opt-ins false. These options are report identity
  // and must be carried by every participating dataset request.
  population?: ExtractPopulation;
  pipeline?: ExtractPipeline;
  // Detaches this caller from the fetch. A `202 building` poll has no client
  // timeout by design, so without this a user who navigates away from a report
  // whose extract is still building leaves the tab polling the API every
  // POLL_MAX_MS for as long as the build takes. Omitted = uncancellable, which
  // is the previous behaviour and what every non-React caller gets.
  //
  // Forced refreshes COALESCE (one `?refresh=1` per dataset+scope), so this
  // signal is not forwarded to the request directly: it joins a
  // SharedCancellation and the shared poll aborts only when every joined caller
  // has aborted. One component unmounting can never cancel a refresh another is
  // still waiting on.
  signal?: AbortSignal;
}

// Rejection produced when a caller (or every caller of a coalesced fetch) aborts.
// Distinct from ExtractBuildFailedError: nothing went wrong, so the runner's
// stale-run guard swallows it rather than rendering an error state.
export class ExtractCancelledError extends Error {
  constructor() {
    super('The extract request was cancelled');
    this.name = 'ExtractCancelledError';
  }
}

// Thrown when the build workflow has failed terminally (HTTP 503, D-202 §8).
// The runner maps it to a retryable `error` state. Distinct from a 202, which
// the poll loop swallows and never surfaces as an error.
export class ExtractBuildFailedError extends Error {
  readonly retryAfterSeconds: number | null;
  constructor(response?: { headers?: Record<string, unknown> }) {
    super('The report build failed');
    this.name = 'ExtractBuildFailedError';
    const seconds = Number(response?.headers?.['retry-after']);
    this.retryAfterSeconds =
      Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  }
}

// The wait between 202 polls, interruptible. An un-interruptible sleep would
// keep a cancelled loop alive for up to POLL_MAX_MS and issue one more request
// after the caller has gone.
const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ExtractCancelledError());
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new ExtractCancelledError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });

// Checked at the top of every poll iteration so an abort that lands while a
// request is in flight stops the loop instead of starting one more round.
const throwIfCancelled = (signal?: AbortSignal): void => {
  if (signal?.aborted) throw new ExtractCancelledError();
};

// Server `Retry-After` (seconds) → the poll FLOOR in ms. A 202 with no or an
// invalid header falls back to POLL_BASE_MS.
const pollFloorMs = (response: {
  headers?: Record<string, unknown>;
}): number => {
  const seconds = Number(response.headers?.['retry-after']);
  return Number.isFinite(seconds) && seconds > 0
    ? seconds * 1000
    : POLL_BASE_MS;
};

// Keyed by requestKey (dataset + scope + population), NOT by dataset: distinct
// windows and population options are distinct server response identities, so a
// shared entry can satisfy a request from the wrong representation.
//
// Scope-keying makes the key space unbounded (a user dragging a date filter mints
// a new key per window, and `relative_date` filters re-resolve daily), and each
// entry retains a whole Parquet ArrayBuffer. Before scope-keying this map held at
// most one buffer per dataset; an LRU keeps that bound close to where it was while
// preserving the property that actually matters — a wide and a narrow window never
// collide. Insertion order is the recency order (a re-read deletes and re-sets).
const MAX_CACHED_SCOPES_PER_DATASET = 3;
const extractCache = new Map<string, ExtractResult>();
// The force generation each cached entry was published at (see `forceGeneration`).
const cacheGeneration = new Map<string, number>();

const storeExtract = (
  datasetKey: string,
  key: string,
  entry: ExtractResult
): ExtractResult => {
  extractCache.delete(key); // re-insert so this key becomes the most recent
  extractCache.set(key, entry);
  cacheGeneration.set(key, forceGeneration.get(datasetKey) ?? 0);
  const prefix = `${datasetKey}\u0000`;
  const mine = [...extractCache.keys()].filter(k => k.startsWith(prefix));
  for (const stale of mine.slice(0, -MAX_CACHED_SCOPES_PER_DATASET)) {
    extractCache.delete(stale);
    cacheGeneration.delete(stale);
  }
  return entry;
};

// COALESCING INVARIANT: at most one forced rebuild is ever outstanding per
// dataset AND SCOPE. `inFlightForce` holds that single in-flight force promise; a second
// forceExtract call while one is in flight returns the *identical* promise
// (`first === second`) and issues no second network call. Because there is never
// more than one force building, the two force-vs-force hazards the earlier
// rounds fought — "an older force overwrites a newer one" and "a force is lost"
// — cannot arise by construction, so the winning force publishes its bytes
// UNCONDITIONALLY with no epoch/displacement bookkeeping.
//
// Coalescing does NOT cover the plain-vs-force boundary: a plain GET can start
// before any force, then a force can start AND finish entirely inside that GET's
// network window (S3 publication is non-atomic, so the plain GET may still read
// pre-rebuild bytes). `forceGeneration` is the lightweight boundary marker for
// exactly that case — a counter bumped only when a NEW coalesced force actually
// starts. A plain fetch samples it before its GET; if it advanced by the time the
// GET returns, a rebuild happened underneath and the plain must not publish its
// possibly-stale bytes.
//
// `inFlightForce` is keyed by (dataset, scope) — those really are separate
// representations — but `forceGeneration` is keyed by DATASET ALONE, because what
// a rebuild republishes is the dataset's partition OBJECTS in S3, which every
// scope reads. Scope-keying the marker made it blind to exactly the hazard it
// exists for: refresh a 12-month report, widen the filter mid-rebuild, and the new
// scope's plain GET sees generation 0 for its own key, publishes pre-rebuild
// bytes, and the render immediately following the user's Refresh click is stale.
//
// The entry carries the force's `SharedCancellation` alongside its promise: every
// coalescing caller joins it, and the poll loop is aborted only once they have
// all detached (see FetchExtractOptions.signal).
interface InFlightForce {
  promise: Promise<ExtractResult>;
  cancellation: SharedCancellation;
}
const inFlightForce = new Map<string, InFlightForce>();
const forceGeneration = new Map<string, number>();

// A force in flight for a DIFFERENT scope of the same dataset. `forceGeneration`
// alone cannot cover this: it detects a rebuild that starts and finishes inside a
// plain GET's window, but a plain fetch that BEGINS after the force already bumped
// the counter samples the bumped value, sees it unchanged afterwards, and publishes
// bytes the server had not rebuilt yet. That is precisely the "refresh a 12-month
// report, widen the filter mid-rebuild, and the render immediately following the
// user's Refresh click is stale" case the counter exists to prevent — the counter
// just cannot see it from this side. Waiting for the other scope's force and then
// refetching closes it: dataset keys cannot contain NUL, so the prefix scan over
// the (dataset, scope) keys is unambiguous, and `inFlightForce` already self-cleans.
const otherScopeForceInFlight = (
  datasetKey: string,
  ownKey: string
): Promise<ExtractResult> | undefined => {
  const prefix = `${datasetKey}\u0000`;
  for (const [candidate, entry] of inFlightForce) {
    if (candidate !== ownKey && candidate.startsWith(prefix))
      return entry.promise;
  }
  return undefined;
};

const buildEntry = (response: {
  headers?: Record<string, unknown>;
  data: unknown;
}): ExtractResult => ({
  etag: (response.headers?.etag as string) ?? '',
  builtAt: (response.headers?.['x-extract-built-at'] as string) ?? null,
  buffer: response.data as ArrayBuffer,
});

const forceRefresh = (
  datasetKey: string,
  scope: ExtractScope,
  opts?: FetchExtractOptions
): Promise<ExtractResult> => {
  const population = opts?.population ?? DEFAULT_EXTRACT_POPULATION;
  const key = requestKey(datasetKey, scope, population, opts?.pipeline);
  // Coalesce: an already-running force is returned verbatim — the same promise,
  // no second network call. The get/set below straddle no await, so the
  // at-most-one-force invariant holds. N coalesced forces therefore share the
  // ONE poll loop below. Coalescing is per (dataset, scope, population):
  // distinct requests are different response identities and cannot publish one
  // result as the other's answer.
  // An entry whose cancellation has already fired is spent — every caller that
  // asked for it has gone and its poll is unwinding — so it must not satisfy a
  // new caller. Falling through starts a fresh force instead.
  const existing = inFlightForce.get(key);
  if (existing && !existing.cancellation.aborted) {
    existing.cancellation.join(opts?.signal);
    return existing.promise;
  }

  // A genuinely new force starts here: advance the DATASET's generation so any
  // plain fetch of ANY scope whose GET overlaps this rebuild can detect it
  // (coalesced no-op forces above must NOT bump it — the marker tracks real
  // rebuilds, not call count).
  forceGeneration.set(datasetKey, (forceGeneration.get(datasetKey) ?? 0) + 1);
  // Drop this scope's cache entry so the refresh request is unconditional (no
  // If-None-Match) and whatever bytes it publishes always replace the cache.
  // OTHER scopes' entries are deliberately left alone: a rebuild changes their
  // bytes too, but each carries its own ETag and every plain fetch revalidates
  // with If-None-Match, so a post-rebuild read of another scope gets a 200 with
  // fresh bytes rather than a stale 304 (D3 folds built_at of the SELECTED
  // entries into the ETag).
  extractCache.delete(key);
  cacheGeneration.delete(key);

  // Every caller of this coalesced force joins here; the shared poll is aborted
  // only when the last one detaches.
  const cancellation = new SharedCancellation();
  cancellation.join(opts?.signal);

  const run = (async (): Promise<ExtractResult> => {
    // The forced rebuild may itself return `202 building` under the async-build
    // path, so poll INSIDE this single coalesced force promise until real bytes
    // arrive. `url` starts as the forced ?refresh=1 and switches to the plain
    // unconditional GET after a 429 cooldown.
    let waitMs = POLL_BASE_MS;
    const startedAt = Date.now();
    let forcedAt: string | undefined;
    let url = extractUrl(datasetKey, scope, {
      refresh: true,
      population,
      pipeline: opts?.pipeline,
    });
    for (;;) {
      throwIfCancelled(cancellation.signal);
      const response = await client.request({
        method: 'GET',
        url,
        responseType: 'arraybuffer',
        signal: cancellation.signal,
        // 429 = server-side refresh cooldown (the extract was rebuilt moments
        // ago) → fall through to a plain fetch of the just-built extract. 202 =
        // still building → poll. 503 = terminal build failure → surface. Any
        // other status throws for the runner to map.
        validateStatus: (status: number) =>
          status === 200 || status === 429 || status === 202 || status === 503,
        throwOnError: true,
      });
      if (response.status === 200) {
        // Unconditional publish is safe: coalescing guarantees this is the only
        // force in flight, so there is no other force whose bytes we could stomp.
        return storeExtract(datasetKey, key, buildEntry(response));
      }
      if (response.status === 503) throw new ExtractBuildFailedError(response);
      if (response.status === 429) {
        // 429 cooldown: unconditional plain GET of the just-built extract. The
        // cache entry was dropped above and this never reads it, so no
        // If-None-Match leaks and the server returns the current bytes (which
        // may themselves still be a 202 under async build — hence the loop).
        // Reset the backoff for the fresh poll.
        url = extractUrl(datasetKey, scope, {
          population,
          pipeline: opts?.pipeline,
        });
        waitMs = POLL_BASE_MS;
        continue;
      }
      // 202 building: the first forced 202 says when the force began. Send it
      // back as `since` so the server can tell when every bucket has been rebuilt
      // since then (a pass longer than the cooldown would otherwise never end),
      // and does not re-queue the buckets on each poll.
      const serverForcedAt = response.headers?.['x-extract-forced-at'];
      if (!forcedAt && typeof serverForcedAt === 'string') {
        forcedAt = serverForcedAt;
        url = extractUrl(datasetKey, scope, {
          refresh: true,
          since: forcedAt,
          population,
          pipeline: opts?.pipeline,
        });
      }
      // Honor the server Retry-After floor, grow the client backoff, sleep, and
      // poll again.
      waitMs = Math.min(Math.max(pollFloorMs(response), waitMs), POLL_MAX_MS);
      opts?.onBuilding?.({
        elapsedMs: Date.now() - startedAt,
        slow: Date.now() - startedAt > SOFT_SLOW_MS,
      });
      await sleep(waitMs, cancellation.signal);
      waitMs = Math.min(waitMs * 1.5, POLL_MAX_MS);
    }
  })().finally(() => {
    // Identity-guarded: a fully-cancelled force is REPLACED in the map (spent
    // entries are skipped above) instead of settling first, so an unguarded
    // delete would let the abandoned force's late settle evict the live one.
    if (inFlightForce.get(key) === entry) inFlightForce.delete(key);
  });

  // Store the cleanup-wrapped promise itself, so a coalescing caller returns the
  // *identical* object (first === second).
  const entry: InFlightForce = { promise: run, cancellation };
  inFlightForce.set(key, entry);
  return run;
};

const fetchConditional = async (
  datasetKey: string,
  scope: ExtractScope,
  opts?: FetchExtractOptions
): Promise<ExtractResult> => {
  const population = opts?.population ?? DEFAULT_EXTRACT_POPULATION;
  const key = requestKey(datasetKey, scope, population, opts?.pipeline);
  // Poll INSIDE this single promise (the layers above coalesce plain ensures, so
  // N callers share ONE loop): a cold-miss `202 building` keeps looping here and
  // resolves only when real bytes (200) or a usable 304 arrive. Callers never
  // observe the 202, so it can never reach the runner's error catch.
  let waitMs = POLL_BASE_MS;
  const startedAt = Date.now();
  for (;;) {
    // Re-checked every iteration, so an abort that lands during a request, a
    // sleep, or a wait on someone else's force ends the loop at the next
    // boundary instead of issuing another poll. Awaiting another caller's force
    // is deliberately NOT interrupted here: that promise is shared and already
    // has its own cancellation, and abandoning the wait would only make this
    // caller re-enter the loop and issue the GET the wait exists to prevent.
    throwIfCancelled(opts?.signal);
    // M5: re-sample the force state on EVERY iteration. A force can start while
    // we sleep between polls, and even a plain 202 poll must yield to it — its
    // rebuilt bytes are authoritative and a plain GET could otherwise capture the
    // pre-rebuild extract the server is still serving. If the force fails, fall
    // through to our own fetch this iteration rather than inheriting its rejection.
    const forceAtStart = inFlightForce.get(key);
    if (forceAtStart) {
      try {
        return await forceAtStart.promise;
      } catch {
        // Force (or its 429 fallback) rejected — it dropped the cache, so fall
        // through to a fresh unconditional fetch.
      }
    }

    // A force is rebuilding a DIFFERENT scope of this dataset. Its bytes are not
    // ours to return (different representation), but the rebuild it is waiting on
    // republishes the partition objects WE read, so a GET issued now can capture
    // the pre-rebuild extract. Wait for it, then re-enter the loop and fetch our
    // own scope against the settled state. Settlement is all we need — a failed
    // force leaves the server no less current than before.
    const otherForce = otherScopeForceInFlight(datasetKey, key);
    if (otherForce) {
      await otherForce.catch(() => undefined);
      continue;
    }

    // Sample the boundary marker before the GET: if a force starts AND finishes
    // inside this GET's window, the generation will have advanced by the time we
    // return, and our bytes may predate that rebuild.
    const generationAtStart = forceGeneration.get(datasetKey) ?? 0;
    const cached = extractCache.get(key);
    // The boundary invariant is narrowed to a *successful* plain response: if this
    // GET itself rejects, the error propagates and the post-response deferral
    // checks below never run. That is correct — a plain caller that never received
    // bytes published nothing to reconcile against the force, so there is nothing
    // to salvage; surfacing the failure is right, not swallowing it into a force's
    // result the caller never observed.
    // Route through client.request (not client.instance) so the configured API
    // baseURL applies — client.instance issues a bare relative request that
    // resolves against the SPA origin and returns index.html with HTTP 200.
    const response = await client.request({
      method: 'GET',
      url: extractUrl(datasetKey, scope, {
        population,
        pipeline: opts?.pipeline,
      }),
      responseType: 'arraybuffer',
      signal: opts?.signal,
      headers: cached ? { 'If-None-Match': cached.etag } : undefined,
      // 200/304 are "ok"; 202 (building) and 503 (terminal build failure) join
      // them as non-throwing so the loop below can branch on them. Anything else
      // (e.g. 413 too-large) throws so the runner can map it.
      validateStatus: (status: number) =>
        status === 200 || status === 304 || status === 202 || status === 503,
      throwOnError: true,
    });
    // A force is building right now (began during our GET): defer to it, again
    // recovering on failure by re-running the loop.
    const forceNow = inFlightForce.get(key);
    if (forceNow) {
      try {
        return await forceNow.promise;
      } catch {
        continue;
      }
    }
    // A force started AND completed during our GET (generation advanced but none
    // is in flight now): our bytes may predate its rebuild, so do NOT publish
    // them. Yield to the force's freshly published cache — but ONLY when that
    // cache entry was itself published at the newer generation. A force on a
    // DIFFERENT scope of this dataset also advances the counter, and this key's
    // entry then still holds pre-rebuild bytes, so it must be dropped and refetched
    // rather than returned.
    if ((forceGeneration.get(datasetKey) ?? 0) !== generationAtStart) {
      const republished = extractCache.get(key);
      if (republished && (cacheGeneration.get(key) ?? 0) > generationAtStart) {
        return republished;
      }
      extractCache.delete(key);
      cacheGeneration.delete(key);
      continue;
    }
    if (response.status === 503) throw new ExtractBuildFailedError(response);
    if (response.status === 202) {
      // Honor the server Retry-After floor, grow the client backoff, sleep, and
      // poll again. No hard timeout: a terminally-failed build returns 503 above.
      waitMs = Math.min(Math.max(pollFloorMs(response), waitMs), POLL_MAX_MS);
      opts?.onBuilding?.({
        elapsedMs: Date.now() - startedAt,
        slow: Date.now() - startedAt > SOFT_SLOW_MS,
      });
      await sleep(waitMs, opts?.signal);
      waitMs = Math.min(waitMs * 1.5, POLL_MAX_MS);
      continue;
    }
    if (response.status === 304 && cached)
      return storeExtract(datasetKey, key, cached);
    return storeExtract(datasetKey, key, buildEntry(response));
  }
};

// Non-async so a forced refresh returns forceRefresh's promise verbatim: two
// concurrent forceExtract calls therefore satisfy `first === second`, not merely
// "same resolved value".
export const fetchExtract = (
  datasetKey: string,
  opts?: FetchExtractOptions
): Promise<ExtractResult> => {
  const scope = opts?.scope ?? FULL_SCOPE;
  if (opts?.force) return forceRefresh(datasetKey, scope, opts);
  return fetchConditional(datasetKey, scope, opts);
};

export const resetExtractCacheForTests = (): void => {
  extractCache.clear();
  cacheGeneration.clear();
  inFlightForce.clear();
  forceGeneration.clear();
};

export const listReportDefinitions = async (): Promise<ReportListItem[]> => {
  const response = await client.request({
    method: 'GET',
    url: '/reporting/report-definitions',
    throwOnError: true,
  });
  return response.data as ReportListItem[];
};

export const createReportDefinition = async (
  payload: ReportDefinitionPayload
): Promise<ReportListItem> => {
  const response = await client.request({
    method: 'POST',
    url: '/reporting/report-definitions',
    body: payload,
    throwOnError: true,
  });
  return response.data as ReportListItem;
};

export const updateReportDefinition = async (
  id: string,
  payload: Partial<ReportDefinitionPayload>
): Promise<ReportListItem> => {
  const response = await client.request({
    method: 'PATCH',
    url: `/reporting/report-definitions/${id}`,
    body: payload,
    throwOnError: true,
  });
  return response.data as ReportListItem;
};

export const deleteReportDefinition = async (id: string): Promise<void> => {
  await client.request({
    method: 'DELETE',
    url: `/reporting/report-definitions/${id}`,
    throwOnError: true,
  });
};

export const listReportWidgets = async (): Promise<ReportWidget[]> => {
  const response = await client.request({
    method: 'GET',
    url: '/reporting/widgets',
    throwOnError: true,
  });
  return response.data as ReportWidget[];
};

export const createReportWidget = async (
  payload: ReportWidgetPayload
): Promise<ReportWidget> => {
  const response = await client.request({
    method: 'POST',
    url: '/reporting/widgets',
    body: payload,
    throwOnError: true,
  });
  return response.data as ReportWidget;
};

export const updateReportWidget = async (
  id: string,
  payload: Partial<
    Pick<ReportWidgetPayload, 'name' | 'form' | 'display' | 'visibility'>
  > & { report_definition_id?: string }
): Promise<ReportWidget> => {
  const response = await client.request({
    method: 'PATCH',
    url: `/reporting/widgets/${id}`,
    body: payload,
    throwOnError: true,
  });
  return response.data as ReportWidget;
};

export const deleteReportWidget = async (id: string): Promise<void> => {
  await client.request({
    method: 'DELETE',
    url: `/reporting/widgets/${id}`,
    throwOnError: true,
  });
};

export const listDashboardTabs = async (): Promise<DashboardTab[]> => {
  const response = await client.request({
    method: 'GET',
    url: '/reporting/dashboard-tabs',
    throwOnError: true,
  });
  return response.data as DashboardTab[];
};

export const createDashboardTab = async (
  name: string
): Promise<DashboardTab> => {
  const response = await client.request({
    method: 'POST',
    url: '/reporting/dashboard-tabs',
    body: { name },
    throwOnError: true,
  });
  return response.data as DashboardTab;
};

export const updateDashboardTab = async (
  id: string,
  payload: { name?: string; position?: number }
): Promise<DashboardTab> => {
  const response = await client.request({
    method: 'PATCH',
    url: `/reporting/dashboard-tabs/${id}`,
    body: payload,
    throwOnError: true,
  });
  return response.data as DashboardTab;
};

export const deleteDashboardTab = async (id: string): Promise<void> => {
  await client.request({
    method: 'DELETE',
    url: `/reporting/dashboard-tabs/${id}`,
    throwOnError: true,
  });
};

export const getDashboardTabWidgets = async (
  tabId: string
): Promise<DashboardTabWidgetItem[]> => {
  const response = await client.request({
    method: 'GET',
    url: `/reporting/dashboard-tabs/${tabId}/widgets`,
    throwOnError: true,
  });
  return response.data as DashboardTabWidgetItem[];
};

export const setDashboardTabWidgets = async (
  tabId: string,
  widgetIds: string[]
): Promise<DashboardTabWidgetItem[]> => {
  const response = await client.request({
    method: 'PUT',
    url: `/reporting/dashboard-tabs/${tabId}/widgets`,
    body: { widget_ids: widgetIds },
    throwOnError: true,
  });
  return response.data as DashboardTabWidgetItem[];
};
